/**
 * Visual progress bar showing path to House majority (218 seats)
 * Uses optimized political color palette
 */
export default function MajorityProgress({ demSeats = 0, repSeats = 0, competitive = 0 }) {
  const majority = 218;
  const total = 435;

  // Calculate percentages
  const demPercent = (demSeats / total) * 100;
  const repPercent = (repSeats / total) * 100;
  const majorityLine = (majority / total) * 100;

  // Seats needed for majority
  const demNeeds = Math.max(0, majority - demSeats);
  const repNeeds = Math.max(0, majority - repSeats);

  return (
    <div className="w-full">
      {/* Progress bar container */}
      <div className="relative h-8 sm:h-10 bg-slate-800/80 rounded-full overflow-hidden border border-slate-600/50 shadow-inner">

        {/* Democratic seats (from left) - Rich blue gradient */}
        <div
          className="absolute left-0 top-0 h-full transition-all duration-700 ease-out"
          style={{
            width: `${demPercent}%`,
            background: 'linear-gradient(90deg, #0D47A1 0%, #1976D2 50%, #2196F3 100%)',
            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.1), 0 0 20px rgba(33, 150, 243, 0.3)'
          }}
        >
          {demSeats > 50 && (
            <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm drop-shadow-lg">
              {demSeats}
            </span>
          )}
        </div>

        {/* Republican seats (from right) - Deep red gradient */}
        <div
          className="absolute right-0 top-0 h-full transition-all duration-700 ease-out"
          style={{
            width: `${repPercent}%`,
            background: 'linear-gradient(90deg, #F44336 0%, #D32F2F 50%, #B71C1C 100%)',
            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.1), 0 0 20px rgba(229, 57, 53, 0.3)'
          }}
        >
          {repSeats > 50 && (
            <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm drop-shadow-lg">
              {repSeats}
            </span>
          )}
        </div>

        {/* Competitive/undecided seats (center) - Electric purple */}
        {competitive > 0 && (
          <div
            className="absolute top-0 h-full transition-all duration-500"
            style={{
              left: `${demPercent}%`,
              width: `${Math.max(0, 100 - demPercent - repPercent)}%`,
              background: 'linear-gradient(90deg, #7B1FA2 0%, #9C27B0 50%, #AB47BC 100%)',
              boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.1)'
            }}
          />
        )}

        {/* Majority line marker - Gold with glow */}
        <div
          className="absolute top-0 h-full w-0.5 sm:w-1 z-10"
          style={{
            left: `${majorityLine}%`,
            transform: 'translateX(-50%)',
            background: 'linear-gradient(180deg, #FFD700 0%, #FFC107 50%, #FFB300 100%)',
            boxShadow: '0 0 10px rgba(255, 193, 7, 0.8), 0 0 20px rgba(255, 193, 7, 0.4)'
          }}
        >
          <div
            className="absolute -top-5 sm:-top-7 left-1/2 -translate-x-1/2 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full whitespace-nowrap text-[10px] sm:text-xs font-bold"
            style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              color: '#1a1a1a',
              boxShadow: '0 2px 8px rgba(255, 193, 7, 0.5)'
            }}
          >
            218
          </div>
        </div>
      </div>

      {/* Legend with improved styling - responsive for mobile */}
      <div className="flex flex-wrap justify-between items-center mt-3 gap-y-2 text-xs sm:text-sm">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div
            className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #1976D2 0%, #2196F3 100%)',
              boxShadow: '0 0 8px rgba(33, 150, 243, 0.5)'
            }}
          />
          <span className="font-semibold whitespace-nowrap" style={{ color: '#64B5F6' }}>
            D: {demSeats}
            {demNeeds > 0 && (
              <span className="text-slate-400 font-normal ml-1 hidden xs:inline">({demNeeds} needed)</span>
            )}
          </span>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <div
            className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
              boxShadow: '0 0 8px rgba(156, 39, 176, 0.5)'
            }}
          />
          <span className="font-semibold whitespace-nowrap" style={{ color: '#CE93D8' }}>
            {competitive} <span className="hidden xs:inline">in play</span><span className="xs:hidden">🎯</span>
          </span>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <span className="font-semibold whitespace-nowrap" style={{ color: '#EF9A9A' }}>
            R: {repSeats}
            {repNeeds > 0 && (
              <span className="text-slate-400 font-normal ml-1 hidden xs:inline">({repNeeds} needed)</span>
            )}
          </span>
          <div
            className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #D32F2F 0%, #F44336 100%)',
              boxShadow: '0 0 8px rgba(229, 57, 53, 0.5)'
            }}
          />
        </div>
      </div>
    </div>
  );
}
